import { useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ButtonSecondary } from '@components/button';
import { BUTTON_VARIANT_OUTLINE } from '@components/button/src/config';
import { Field } from '@components/dynamic-input-fields/src/dynamic-input-fields.type';
import { useSortedFields } from '@components/dynamic-input-fields/src/utils';
import { InputCheckbox } from '@components/input-checkbox';
import { InputCheckboxEditable } from '@components/input-checkbox-editable';
// import { InputCheckboxEditable } from '@components/input-checkbox-editable';
import { LabelPrimary } from '@components/label';
import { ModalDynamicFormField } from '@components/modals/modal-dynamic-form-field';
import { camelize } from '@utils/camel-case-string';

import { InputFormFieldsProps } from './input-form-fields.type';

export const InputFormFields = ({
  label,
  fields,
  formFieldName,
  disableConditions,
  showModal,
  appendFields,
}: InputFormFieldsProps) => {
  const [isDynamicFieldModalOpen, setIsDynamicFieldModalOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const { control } = useFormContext();
  // Keep indexes to be able to sort array: https://github.com/orgs/react-hook-form/discussions/4264#discussioncomment-398509
  const fieldsIndexes = useMemo(() => new Map(fields.map(({ id }, index) => [id, index])), [fields]);
  const sortedFields = useSortedFields(fields, fieldsIndexes);
  const showDynamicInputFields = showModal && appendFields;
  return (
    <>
      {label && <LabelPrimary>{label}</LabelPrimary>}
      <div className='mt-5 m-1 grid grid-cols-2 gap-4 lg:grid-cols-4'>
        {sortedFields.map((formField) => {
          const nameFormatted = camelize(formField.name as string);
          const fieldLabel = i18n.exists(`offers:${nameFormatted}FormfieldLabel`)
            ? t(`offers:${nameFormatted}FormfieldLabel`)
            : formField.name;
          const index = fieldsIndexes.get(formField.id);
          return (
            <Controller
              key={formField.id}
              name={`${formFieldName}.${index}.isRequired`}
              control={control}
              render={({ field }) => (
                <InputCheckbox
                  name={`${formFieldName}.${index}`}
                  label={fieldLabel}
                  value={field.value}
                  disabled={formField.disabled || disableConditions}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                />
              )}
            />
          );
        })}
        {!showModal && appendFields && <InputCheckboxEditable onChange={appendFields} disabled={disableConditions} />}
        {showDynamicInputFields && (
          <div className=''>
            <ButtonSecondary
              disabled={disableConditions}
              variant={BUTTON_VARIANT_OUTLINE}
              onClick={() => setIsDynamicFieldModalOpen(true)}>
              + {t('common:addNewButton')}
            </ButtonSecondary>
          </div>
        )}
      </div>
      {showDynamicInputFields && (
        <ModalDynamicFormField
          isOpen={isDynamicFieldModalOpen}
          primaryActionText={t('common:save')}
          secondaryActionText={t('common:cancel')}
          onPrimaryAction={(field: Omit<Field, 'id'>) => {
            appendFields(field.name, field.max, field.min, field.dataType);
            setIsDynamicFieldModalOpen(false);
          }}
          handleClose={() => setIsDynamicFieldModalOpen(false)}
        />
      )}
    </>
  );
};
